import {Checkbox, ListItemText, FormControlLabel, Stack} from '@mui/material/';

import {StyledAdornedEnd} from 'styles/InputMenu';
import InputActionButtons from './InputActionButtons';

import NumberFormatCustom from 'helpers/NumberFormatter';

import {type HarvestDemandPutCertificatesEnum, type HarvestDemandPutSpeciesEnum} from 'client';

import {GenericSelect, GenericSelectItem} from 'components/Generic/Selectors';
import GenericTextField from 'components/Generic/TextField';
import {useTranslation} from 'react-i18next';

import {SpeciesSelector} from './SpeciesSelector';
import {CertificatesSelector} from './CertificatesSelector';
import {InputMenuDatePicker} from 'components/Generic/DatePickersV2/InputMenuDatePicker';
import {CompanySelector} from 'components/Shared/CompanySelector';
import {useAddEditSmoltSupply} from 'hooks/addEdit/useAddEditSmoltSupply';
import {FishGroupPropertiesTags} from 'components/Shared/FishGroupPropertiesTags';

const SmoltAvailability = () => {
  const {t} = useTranslation('common');
  const {
    smoltSupply,
    setSmoltSupply,
    planId,
    fishTypes,
    planTreeNavigation,
    smoltForm2Write,
    setCreateUpdateSmoltAvailability,
    isLoading,
  } = useAddEditSmoltSupply();
  const siteid2name = new Map((planTreeNavigation?.sites ?? []).map<[string, string]>((site) => [site.id, site.name]));

  return (
    <form noValidate autoComplete="off" style={{width: '100%'}}>
      <Stack spacing={2}>
        {/* Name */}
        <GenericTextField
          label={t('name')}
          value={smoltSupply.name}
          onChange={(event) => {
            setSmoltSupply({...smoltSupply, name: event.target.value});
          }}
          inputProps={{'data-testid': 'smolt-avalability-menu-name'}}
        />

        {/* Company */}
        <CompanySelector
          planId={planId}
          companyId={smoltSupply.companyId ?? ''}
          setCompanyId={(companyId) => {
            setSmoltSupply({...smoltSupply, companyId});
          }}
        />
        {/* Fishtype picker */}
        <GenericSelect
          value={smoltSupply.fishTypeId ?? ''}
          onChange={(event) => {
            const fishTypeId = event.target.value as string;
            setSmoltSupply({...smoltSupply, fishTypeId});
          }}
          label={t('fishType')}
          data-testid="smolt-avalability-menu-fish-type"
        >
          {fishTypes &&
            fishTypes.map((fishType) => (
              <GenericSelectItem key={fishType.id} value={fishType.id}>
                {fishType.name}
              </GenericSelectItem>
            ))}
        </GenericSelect>

        {/* Count */}
        <GenericTextField
          InputProps={{
            inputComponent: NumberFormatCustom as (props) => JSX.Element,
            endAdornment: <StyledAdornedEnd>{t('fish')}</StyledAdornedEnd>,
          }}
          label={t('count')}
          value={smoltSupply.count && smoltSupply.count > 0 ? smoltSupply.count : ''}
          onChange={(event) => {
            setSmoltSupply({...smoltSupply, count: parseInt(event.target.value)});
          }}
          inputProps={{'data-testid': 'smolt-avalability-menu-count'}}
        />

        {/* Weight */}
        <GenericTextField
          InputProps={{
            inputComponent: NumberFormatCustom as (props) => JSX.Element,
            endAdornment: <StyledAdornedEnd>{t('gramsAbbreviated')}</StyledAdornedEnd>,
          }}
          label={t('weight')}
          value={smoltSupply.weight && smoltSupply.weight > 0 ? smoltSupply.weight : ''}
          onChange={(event) => {
            setSmoltSupply({...smoltSupply, weight: parseFloat(event.target.value)});
          }}
          inputProps={{'data-testid': 'smolt-avalability-menu-weight'}}
        />

        {/* Start Date */}
        <InputMenuDatePicker
          slotProps={{
            textField: {
              inputProps: {
                'data-testid': 'smolt-avalability-menu-start-date',
              },
            },
          }}
          value={smoltSupply.startDate}
          onChange={(date) => {
            setSmoltSupply({...smoltSupply, startDate: date});
          }}
          label={t('startDate')}
        />
        {/* End Date */}
        <InputMenuDatePicker
          slotProps={{
            textField: {
              inputProps: {
                'data-testid': 'smolt-avalability-menu-end-date',
              },
            },
          }}
          value={smoltSupply.endDate}
          onChange={(date) => {
            setSmoltSupply({...smoltSupply, endDate: date});
          }}
          label={t('endDate')}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={!!smoltSupply.assignedToAll}
              onChange={() => {
                setSmoltSupply({...smoltSupply, siteIds: [], assignedToAll: !smoltSupply.assignedToAll});
              }}
              data-testid="smolt-avalability-menu-all-sites"
            />
          }
          label={t('allSites')}
        />

        {planTreeNavigation && !smoltSupply.assignedToAll && (
          <GenericSelect
            label={t('sites')}
            multiple
            value={smoltSupply.siteIds ?? []}
            disabled={smoltSupply.assignedToAll}
            onChange={(event) => {
              setSmoltSupply({...smoltSupply, siteIds: event.target.value as string[]});
            }}
            renderValue={(siteIds) => {
              return (
                <span style={{whiteSpace: 'normal'}}>
                  {(siteIds as string[]).map((siteId) => siteid2name.get(siteId)).join(', ')}
                </span>
              );
            }}
            data-testid="smolt-avalability-menu-sites"
          >
            {planTreeNavigation.sites?.map((site) => (
              <GenericSelectItem key={site.id} value={site.id}>
                <Checkbox checked={smoltSupply.siteIds?.includes(site.id) ?? false} />
                <ListItemText primary={site.name} />
              </GenericSelectItem>
            ))}
          </GenericSelect>
        )}

        {/* Fish Species */}
        <SpeciesSelector
          species={smoltSupply?.species}
          setSpecies={(species?: HarvestDemandPutSpeciesEnum) => {
            if (species) {
              setSmoltSupply({...smoltSupply, species});
            }
          }}
        />

        {/* Fish Certificates */}
        <CertificatesSelector
          certificates={smoltSupply?.certificates ?? []}
          setCertificates={(certificates?: HarvestDemandPutCertificatesEnum[]) => {
            setSmoltSupply({...smoltSupply, certificates: certificates ?? []});
          }}
        />

        <FishGroupPropertiesTags
          selectedTags={smoltSupply.fishGroupPropertyIds}
          onAfterCreate={(newProperty) => {
            setSmoltSupply({...smoltSupply, fishGroupPropertyIds: [...smoltSupply.fishGroupPropertyIds, newProperty]});
          }}
          onSelectionChange={(selectedFishGroupProperties) => {
            setSmoltSupply({...smoltSupply, fishGroupPropertyIds: selectedFishGroupProperties});
          }}
          label={t('fishGroupProperties')}
          helperText={t('fishGroupPropertiesHelperText')}
        />

        <InputActionButtons
          disabled={!smoltForm2Write(smoltSupply, planId)}
          setCreateUpdateInput={setCreateUpdateSmoltAvailability}
          loading={isLoading}
        />
      </Stack>
    </form>
  );
};

export default SmoltAvailability;
